/**
 * Frontend build number - the single source of truth.
 *
 * `FRONTEND_BUILD` is a static number bumped by hand on every frontend change.
 * It lives in `shared/` for the same reason languages.js does: both sides need
 * the same number and a second hardcoded copy would drift. The difference is
 * *when* each side reads it:
 *
 *   - the browser carries the number that was in this file when `vite build` ran
 *     (vite bakes the import into the bundle)
 *   - the server reads the number that is in this file right now
 *
 * The frontend polls `GET /api/v1/version` every few seconds. Server number is
 * higher than the bundle's number => that tab is running an old build => show
 * the refresh prompt and pull the new build from the server.
 *
 * DEPLOY ORDER MATTERS: bump the number, then build the frontend, then restart
 * the backend. Bumping and restarting the backend *without* shipping a new
 * frontend build leaves every open tab prompting for a refresh that cannot fix
 * anything (the prompt says so after one attempt, but it is still noise).
 *
 * Bumping = two edits in this file, nothing else anywhere:
 *   1. add an entry at the TOP of RELEASES describing what changed - that list
 *      is what the popup shows the user
 *   2. increase FRONTEND_BUILD to match that entry's `build`
 *
 * Dependency-free, like everything else in `shared/` - see articleExtractor.js.
 */

/**
 * Release notes, newest first. `build` ties an entry to FRONTEND_BUILD; the
 * popup lists every entry newer than the build the browser is running, so the
 * user sees what they are refreshing for.
 *
 * Keeping an entry here is optional - a bump without one still prompts, just
 * without the detail.
 */
export const RELEASES = [
  {
    build: 9,
    version: '1.0.8',
    date: '2026-10-20',
    changes: [
      'Admins and super admins open the shorts page on the full listing, with no default filters',
    ],
  },
  {
    build: 8,
    version: '1.0.7',
    date: '2026-10-20',
    changes: [
      'Shorts page now opens filtered to your own shorts, falling back to your language when you have none',
    ],
  },
  {
    build: 7,
    version: '1.0.6',
    date: '2026-10-20',
    changes: [
      'Developer Options panels on a news page are now equal width when stacked on mobile',
    ],
  },
  {
    build: 6,
    version: '1.0.5',
    date: '2026-10-20',
    changes: [
      'Every news card now has an Edit Short button on the image, which opens the editor directly',
    ],
  },
  {
    build: 5,
    version: '1.0.4',
    date: '2026-10-20',
    changes: [
      'Seen receipt in bug report chats now shows the time it was read',
    ],
  },
  {
    build: 4,
    version: '1.0.3',
    date: '2026-10-20',
    changes: [
      'Bug report chats now show Sent / Seen receipts on your last message',
      'An open chat refreshes on its own, so new replies arrive without a reload',
    ],
  },
  {
    build: 3,
    version: '1.0.2',
    date: '2026-10-20',
    changes: [
      'Bug report chat now supports image and PDF attachments',
      'Image links shared in a bug report chat render inline in the conversation',
    ],
  },
  {
    build: 2,
    version: '1.0.1',
    date: '2026-10-19',
    changes: [
      'Build number now shown at the bottom of the sidebar',
      'Bug reports record which build the reporter was on',
    ],
  },
  {
    build: 1,
    version: '1.0.0',
    date: '2026-10-19',
    changes: [
      'Build version check added - the panel now prompts when a new build is live',
    ],
  },
];

/**
 * The static build number - the only value that is compared, so it only ever
 * goes up. Must match RELEASES[0].build.
 */
export const FRONTEND_BUILD = 9;

/**
 * Human-readable version, display only. Derived from the newest release so it
 * cannot disagree with the list above.
 */
export const FRONTEND_VERSION = RELEASES[0]?.version || '1.0.0';

/**
 * Release notes newer than the build the client is running, capped so a client
 * that has been open across many deploys does not get an endless list.
 *
 * Unknown/garbage client build => just the newest entry.
 */
export const getChangesSince = (clientBuild, limit = 5) => {
  const since = Number.isFinite(clientBuild) ? clientBuild : FRONTEND_BUILD - 1;

  return RELEASES.filter((release) => release.build > since && release.build <= FRONTEND_BUILD)
    .slice(0, limit);
};
